import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import axios, { AxiosError } from 'axios';
import { receiveAxiosError } from '@konicaminolta/common-react';
import { Report } from '../types/Report/Report';
import { REQUEST_REPORTS, RECEIVE_REPORTS, REQUEST_REPORT_DETAILS, RECEIVE_REPORT_DETAILS, UNLOAD_REPORT_DETAILS, ReportActionTypes, REQUEST_REPORT_EMBEDDED_VIEW, RECEIVE_REPORT_EMBEDDED_VIEW, UNLOAD_REPORT_EMBEDDED_VIEW, SET_REPORT_EMBEDDED_COMPONENT, UNLOAD_REPORT_EMBEDDED_COMPONENT } from '../types/actions';
import { CALL_HISTORY_METHOD } from 'connected-react-router';
import { EmbeddedRecordAccess } from '../types/Common/EmbeddedRecordAccess';
import { Embed } from 'powerbi-client';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface ReportState {
    data: Report[];
    embeddedReportDetailsData: EmbeddedRecordAccess[];
    isLoadingData: boolean;
    isLoadedData: boolean;
    hasPermissionsToList: boolean;
    isLoadingDetails: boolean;
    isLoadedDetails: boolean;
    hasPermissionsToDetails: boolean;
    isLoadingEmbeddedViewDetails: boolean;
    isLoadedEmbeddedViewDetails: boolean;
    powerBiEmbeddedReport: Embed | undefined;
}

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
    requestReports: (): AppThunkAction<ReportActionTypes> => (dispatch, getState) => {

        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.report !== undefined 
            && !appState.report.isLoadingData 
            && !appState.report.isLoadedData) 
        {
            dispatch({ type: REQUEST_REPORTS });

            axios
                .request<Report[]>({
                    url: `./api/reports`
                })
                .then((response: { data: Report[]; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_REPORTS, data, hasPermissionsToList: true });
                })
                .catch((e: AxiosError) => {

                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_REPORTS, data: [], hasPermissionsToList: false });
                        return;
                    }

                    dispatch(receiveAxiosError({error: e}));
                });
        }
    },

    requestReportDetails: (reportId: string): AppThunkAction<ReportActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.report !== undefined 
            && !appState.report.isLoadingDetails 
            && !appState.report.isLoadedDetails) 
        {

            dispatch({ type: REQUEST_REPORT_DETAILS });

            axios
                .request<Report>({
                    url: `./api/reports/${reportId}`
                })
                .then((response: { data: Report; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_REPORT_DETAILS, report: data, hasPermissionsToDetails: true });
                })
                .catch((e: AxiosError) => {
                  
                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_REPORT_DETAILS, hasPermissionsToDetails: false });
                        return;
                    }

                    dispatch(receiveAxiosError({error: e}));
                    dispatch({ type: RECEIVE_REPORT_DETAILS });
                    // if a specific resource is not found redirect to resource index page
                    if(e.response && e.response.status >= 400 && e.response.status < 500){
                        dispatch(
                            { 
                                type: CALL_HISTORY_METHOD, 
                                payload: { method: 'push', 'args': [ `/reports` ] }
                            }
                        );
                    }
                });

        }
    },

    unloadReportDetails: (): AppThunkAction<ReportActionTypes> => (dispatch) => {

        dispatch({ type: UNLOAD_REPORT_DETAILS });
    },

    requestReportMinimalDetails: (reportId: string): AppThunkAction<ReportActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.report !== undefined 
            && !appState.report.isLoadingDetails 
            && !appState.report.isLoadedDetails) 
        {

            dispatch({ type: REQUEST_REPORT_DETAILS });

            axios
                .request<Report>({
                    url: `./api/reports/viewreport/${reportId}/details`
                })
                .then((response: { data: Report; }) => {
                    const { data } = response;
                    dispatch({ type: RECEIVE_REPORT_DETAILS, report: data, hasPermissionsToDetails: true });
                })
                .catch((e: AxiosError) => {

                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_REPORT_DETAILS, hasPermissionsToDetails: false });
                        return;
                    }

                    dispatch(receiveAxiosError({error: e}));
                    dispatch({ type: RECEIVE_REPORT_DETAILS, hasPermissionsToDetails: false });

                    // if a specific resource is not found redirect to resource index page
                    if(e.response && e.response.status >= 400 && e.response.status < 500){
                        dispatch(
                            { 
                                type: CALL_HISTORY_METHOD, 
                                payload: { method: 'push', 'args': [ `/reports` ] }
                            }
                        );
                    }
                });

        }
    },

    requestReportEmbeddedViewDetails: (reportId: string): AppThunkAction<ReportActionTypes> => (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)
        const appState = getState();

        if (appState 
            && appState.report !== undefined 
            && !appState.report.isLoadingEmbeddedViewDetails 
            && !appState.report.isLoadedEmbeddedViewDetails) 
        {
            let embeddedReportDetailViewData = appState.report.embeddedReportDetailsData.find(x => x.recordId === reportId);

            if(embeddedReportDetailViewData === undefined){
                dispatch({ type: REQUEST_REPORT_EMBEDDED_VIEW });

                axios
                    .request<EmbeddedRecordAccess>({
                        url: `./api/reports/viewreport/${reportId}`
                    })
                    .then((response: { data: EmbeddedRecordAccess; }) => {
                        const { data } = response;
                        dispatch({ type: RECEIVE_REPORT_EMBEDDED_VIEW, data: data });
                    })
                    .catch((e: AxiosError) => {

                    if (e.response && e.response.status == 401) {
                        dispatch({ type: RECEIVE_REPORT_EMBEDDED_VIEW });
                        return;
                    }

                        dispatch(receiveAxiosError({error: e}));
                        dispatch({ type: RECEIVE_REPORT_EMBEDDED_VIEW });

                        // if a specific resource is not found redirect to resource index page
                        if(e.response && e.response.status >= 400 && e.response.status < 500){
                            dispatch(
                                { 
                                    type: CALL_HISTORY_METHOD, 
                                    payload: { method: 'push', 'args': [ `/reports` ] }
                                }
                            );
                        }
                    });
            }
            else{
                dispatch({ type: RECEIVE_REPORT_EMBEDDED_VIEW });
            }
        }
    },

    unloadReportEmbeddedViewDetails: (): AppThunkAction<ReportActionTypes> => (dispatch) => {
        dispatch({ type: UNLOAD_REPORT_EMBEDDED_VIEW });
    },

    setReportEmbeddedComponent: (embeddedComponent: Embed): AppThunkAction<ReportActionTypes> => (dispatch) => {
        dispatch({type: SET_REPORT_EMBEDDED_COMPONENT, powerBiEmbeddedReport: embeddedComponent});
    },

    unloadReportEmbeddedComponent: () : AppThunkAction<ReportActionTypes> => (dispatch) => {
        dispatch({type: UNLOAD_REPORT_EMBEDDED_COMPONENT});
    }

};

export const reducer: Reducer<ReportState> = (state: ReportState | undefined, incomingAction: Action): ReportState => {
    if (state === undefined) {
        return {
            data: [],
            embeddedReportDetailsData: [],
            isLoadingData: false,
            isLoadedData: false,
            hasPermissionsToList: false,
            isLoadingDetails: false,
            isLoadedDetails: false,
            isLoadedEmbeddedViewDetails: false,
            isLoadingEmbeddedViewDetails: false,
            hasPermissionsToDetails: false,
            powerBiEmbeddedReport: undefined,
        };
    }
    const action = incomingAction as ReportActionTypes;

    switch (action.type) {
        case REQUEST_REPORTS: {
            return {
                ...state,
                isLoadingData: true,
                isLoadedData: false,
            };
        }
        case RECEIVE_REPORTS: {
            return {
                ...state,
                isLoadingData: false,
                isLoadedData: true,
                data: action.data,
                hasPermissionsToList: action.hasPermissionsToList ?? false,
            };
        }
        case REQUEST_REPORT_DETAILS: {
            return {
                ...state,
                isLoadingDetails: true,
                isLoadedDetails: false,
            };
        }
        case RECEIVE_REPORT_DETAILS: {
            // spread operator creates a shallow copy which will work for this specific transform of the state
            // since no nested properties are being affected
            let hasPermissionsToDetails = action.hasPermissionsToDetails;
            let reports = [...state.data];
            if(action.report !== undefined) {
                let reportIndex = reports.findIndex(x => x.id == action.report?.id);
                if (reportIndex !== -1) {
                    reports[reportIndex] = action.report;
                }
                else {
                    reports.push(action.report);
                }
            }

            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: true,
                data: reports,
                hasPermissionsToDetails: hasPermissionsToDetails ?? true,
            };
        }
        case UNLOAD_REPORT_DETAILS: {
            return {
                ...state,
                isLoadingDetails: false,
                isLoadedDetails: false,
            };
        }
        case REQUEST_REPORT_EMBEDDED_VIEW: {
            return {
                ...state,
                isLoadingEmbeddedViewDetails: true,
                isLoadedEmbeddedViewDetails: false,
            };
        }
        case RECEIVE_REPORT_EMBEDDED_VIEW: {
            let embededReportDetail = [...state.embeddedReportDetailsData];
            if(action.data !== undefined){
                embededReportDetail.push(action.data);
            }
            return {
                ...state,
                isLoadingEmbeddedViewDetails: false,
                isLoadedEmbeddedViewDetails: true,
                embeddedReportDetailsData: embededReportDetail,
            };
        }
        case UNLOAD_REPORT_EMBEDDED_VIEW: {
            return {
                ...state,
                isLoadedEmbeddedViewDetails: false,
                isLoadingEmbeddedViewDetails: false,
            };
        }
        case SET_REPORT_EMBEDDED_COMPONENT: {
            return {
                ...state,
                powerBiEmbeddedReport: action.powerBiEmbeddedReport,
            };
        }
        case UNLOAD_REPORT_EMBEDDED_COMPONENT: {
            return {
                ...state,
            };
        }
    }

    return state;
};
